import React, {useEffect, useState} from 'react';
import {
  Modal,
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
} from '@mui/material';
import {apiClient} from '@app/ui/http/query-client';
import {DriveEntry} from '../../drive-entry';
import {useDriveStore} from '@app/drive/drive-store';
import {toast} from '@app/ui/toast/toast';
import {useAccount} from 'wagmi';
import { fetchDocumentTypeByDiskPrefix } from '../../services/signature-api';
const modalStyle = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
};

interface MintDocDialogProps {
  entries: DriveEntry[];
}

export function MintDocDialog({entries}: MintDocDialogProps) {
  const { address } = useAccount();
  const setActiveActionDialog = useDriveStore(s => s.setActiveActionDialog);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    docName: '',
    numberOfCopies: '',
    date: '',
    author: '',
  });
  const [message, setMessage] = useState('');
  const [showNormalMsg, setSowNormalMsg] = useState(false);

  // Get the document type from the selected entry
  const selectedEntry = entries[0];
  const [documentType, setDocumentType] = useState<string>('Loading...');

  useEffect(() => {
    const fetchDocumentType = async () => {
      if (!selectedEntry) return;

      try {
        // Try to get document type from database using disk_prefix
        const diskPrefix = (selectedEntry as any).disk_prefix || (selectedEntry as any).docprefix;
        if (diskPrefix) {
          const dbDocType = await fetchDocumentTypeByDiskPrefix(diskPrefix);
          if (dbDocType) {
            setDocumentType(dbDocType);
            return;
          }
        }

        // Fallback to entry's document_type field
        const fallbackType = (selectedEntry as any)?.document_type || 'Unknown';
        setDocumentType(fallbackType);
      } catch (error) {
        console.error('Error fetching document type:', error);
        setDocumentType((selectedEntry as any)?.document_type || 'Unknown');
      }
    };

    fetchDocumentType();
  }, [selectedEntry]);

  useEffect(() => {
    setSowNormalMsg(true);
    const fetchData = async () => {
      try {
        const payload = { id: entries[0]?.id };
        const response = await apiClient.post('minted-document', payload);
        if (response?.data?.minted && response?.data?.message) {
          setMessage(response.data.message);
          setSowNormalMsg(false);
        }
        if (response?.data?.status && response?.data?.message) {
          setMessage(response.data.message);
        }
        if (!response?.data?.current_balance_status) {
          setMessage(
            'Your current balance is ≤ 0. Please add funds to proceed.'
          );
        }
      } catch (err) {
        console.error('Error fetching minted document status:', err);
      }
    };
    fetchData();
  }, [entries]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | {name?: string; value: unknown}>
  ) => {
    const {name, value} = e.target;
    setFormData({...formData, [name as string]: value});
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form fields
    if (!formData.name || !formData.numberOfCopies || !formData.date || !formData.author) {
      toast.danger('Please fill in all required fields.');
      return;
    }

    if (message) {
      toast.danger(message);
      return;
    }

    setLoading(true);
    try {
      // Get authentication information from localStorage (consistent with other parts of the app)
      const authType = localStorage.getItem('authType');
      const userEmail = localStorage.getItem('userEmail');
      let walletAddress = address;
      let email = userEmail;

      // If we don't have wallet address from wagmi, try to get it from localStorage or user data
      if (!walletAddress) {
        // Try to get wallet address from the current user context or localStorage
        console.log('No wallet address from wagmi, checking user data...');

        // For wallet authentication, the address should be available via wagmi
        // For email authentication, we might need to get wallet address from user data
        if (authType === 'gmail' && userEmail) {
        // For email auth, try to get the associated wallet address from digital_wallets table
        if (authType === 'gmail' && userEmail) {
          try {
            const walletResponse = await apiClient.get(`digital-wallets/by-email/${encodeURIComponent(userEmail)}`);
            if (walletResponse.data && walletResponse.data.wallet_address) {
              walletAddress = walletResponse.data.wallet_address;
              console.log('Found wallet address from digital_wallets:', walletAddress);
            }
          } catch (walletError) {
            console.warn('Could not fetch wallet address for email:', walletError);
          }
        }
        }
      }

      // Ensure we have at least one form of identification
      if (!walletAddress && !email) {
        toast.danger('Authentication information not found. Please log in again.');
        return;
      }

      const payload = {
        ...formData,
        docName: documentType, // Use the document type from the entry
        id: entries[0]?.id,
        minted_doc: `Will: ${entries[0]?.name}`,
        wallet_address: walletAddress,
        email: email,
      };

      console.log('Minting payload:', payload);
      const response = await apiClient.post('minted', payload);
      console.log('Minting response:', response);

      if (response.status === 200 || response.status === 201) {
        toast.positive(response.data.message || 'Document minting started successfully!');
        handleClose();
        // Refresh the page to show updated data
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    } catch (error: any) {
      console.error('Minting error:', error);
      const errorMessage = error?.response?.data?.message || 'Failed to mint document. Please try again later.';
      toast.danger(errorMessage);
      setMessage(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    console.log('handleClose called'); // Debug log
    // Reset form data
    setFormData({
      name: '',
      docName: '',
      numberOfCopies: '',
      date: '',
      author: '',
    });
    setMessage('');
    setSowNormalMsg(false);
    setLoading(false);
    // Close the dialog using the store hook
    console.log('Closing dialog by setting activeActionDialog to null');
    setActiveActionDialog(null);
  };

  return (
    <Modal
      open={true}
      onClose={handleClose}
      aria-labelledby="form-modal-title"
      disableEscapeKeyDown={false}
      keepMounted={false}
    >
      <Box sx={modalStyle}>
          <Typography
            id="form-modal-title"
            variant="h6"
            component="h2"
            gutterBottom
          >
            Minted Form
          </Typography>
          
          {message && (
            <Typography 
              variant="body2" 
              sx={{fontSize: '15px', color: 'red', mb: 2}}
            >
              {message}
            </Typography>
          )}
          
          {showNormalMsg && (
            <Typography 
              variant="body2" 
              sx={{fontSize: '15px', color: 'red', mb: 2}}
            >
              Caution: After minting the document, adding signers is no longer
              possible.
            </Typography>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              disabled={loading || message ? true : false}
              label="Name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              fullWidth
              margin="normal"
              required
            />

            {/* Doc Type Readonly Field */}
            <TextField
              label="Doc Type"
              name="docType"
              value={documentType}
              InputProps={{
                readOnly: true,
              }}
              fullWidth
              margin="normal"
              helperText="Document type is automatically set from the selected document"
            />

            <TextField
              disabled={loading || message ? true : false}
              label="Number of Copies"
              name="numberOfCopies"
              type="number"
              value={formData.numberOfCopies}
              onChange={handleInputChange}
              fullWidth
              margin="normal"
              required
            />

            <TextField
              disabled={loading || message ? true : false}
              label="Date"
              name="date"
              type="date"
              value={formData.date}
              onChange={handleInputChange}
              InputLabelProps={{shrink: true}}
              fullWidth
              margin="normal"
              required
            />

            <TextField
              disabled={loading || message ? true : false}
              label="Author"
              name="author"
              value={formData.author}
              onChange={handleInputChange}
              fullWidth
              margin="normal"
              required
            />

            <Box mt={2} display="flex" justifyContent="flex-end">
              <Button
                variant="outlined"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('Cancel button clicked'); // Debug log
                  handleClose();
                }}
                style={{marginRight: 8}}
                disabled={loading}
                type="button"
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                type="submit"
                color="primary"
                disabled={loading || !!message}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Submit'
                )}
              </Button>
            </Box>
          </form>
      </Box>
    </Modal>
  );
}
